// features/pos/components/ReceiptModal.tsx
// HIGH-SPEED, NON-BLOCKING RECEIPT PRINTING WITH KEYBOARD SHORTCUTS

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircle, Printer, X, ShoppingCart, Shield, Lock,
    AlertCircle, Copy, CheckCheck, ToggleLeft, ToggleRight, MessageSquare
} from 'lucide-react';
import { getReceiptHtml } from '../services/posApi';
import apiClient from '@/lib/api-client';
import { clsx } from 'clsx';

interface ReceiptModalProps {
    isOpen: boolean;
    onClose: () => void;
    onNewSale: () => void;
    tenantId: string;
    saleId: string;
    total: number;
}

export function ReceiptModal({ isOpen, onClose, onNewSale, tenantId, saleId, total }: ReceiptModalProps) {
    const [receiptHtml, setReceiptHtml] = useState('');
    const [printCount, setPrintCount] = useState(0);
    const [showReprintPin, setShowReprintPin] = useState(false);
    const [reprintPin, setReprintPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [isVerifyingPin, setIsVerifyingPin] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [autoPrintEnabled, setAutoPrintEnabled] = useState(() => {
        return localStorage.getItem('pos_auto_print') === 'true';
    });

    const receiptHtmlRef = useRef('');
    const pinInputRef = useRef<HTMLInputElement>(null);
    const printedOnceForSaleRef = useRef<string | null>(null);

    // Keep ref in sync
    useEffect(() => {
        receiptHtmlRef.current = receiptHtml;
    }, [receiptHtml]);

    // Single non-blocking print function
    const executePrint = useCallback((copies: number = 1) => {
        const html = receiptHtmlRef.current;
        if (!html) return;

        setIsPrinting(true);

        // Remove any prior print iframes
        const existing = document.getElementById('pos-receipt-print-frame');
        if (existing && existing.parentNode) {
            existing.parentNode.removeChild(existing);
        }

        const iframe = document.createElement('iframe');
        iframe.id = 'pos-receipt-print-frame';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.style.opacity = '0';
        iframe.style.pointerEvents = 'none';
        iframe.style.zIndex = '-1';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow?.document;
        if (iframeDoc) {
            iframeDoc.open();
            iframeDoc.write(html);
            iframeDoc.close();

            // Trigger print once without recursive loops
            setTimeout(() => {
                try {
                    iframe.contentWindow?.focus();
                    iframe.contentWindow?.print();
                } catch (err) {
                    console.warn('[ReceiptModal] Print trigger notice:', err);
                } finally {
                    setIsPrinting(false);
                    setPrintCount(prev => prev + copies);
                    setShowSuccess(true);
                    setTimeout(() => setShowSuccess(false), 2000);

                    // Safely clean up iframe after print dialog resolves
                    setTimeout(() => {
                        if (iframe && iframe.parentNode) {
                            iframe.parentNode.removeChild(iframe);
                        }
                    }, 2500);
                }
            }, 250);
        } else {
            setIsPrinting(false);
        }
    }, []);

    // Load receipt HTML and handle optional auto-print ONCE per saleId
    useEffect(() => {
        if (isOpen && saleId) {
            setReceiptHtml('');
            receiptHtmlRef.current = '';
            setPrintCount(0);
            setShowReprintPin(false);
            setReprintPin('');
            setPinError('');
            setShowSuccess(false);
            setCopied(false);
            setIsPrinting(false);

            getReceiptHtml(tenantId, saleId)
                .then(html => {
                    setReceiptHtml(html);
                    receiptHtmlRef.current = html;

                    // Only auto-print if enabled and not already printed for this sale
                    if (autoPrintEnabled && printedOnceForSaleRef.current !== saleId) {
                        printedOnceForSaleRef.current = saleId;
                        setTimeout(() => {
                            executePrint(1);
                        }, 200);
                    }
                })
                .catch(() => {
                    setReceiptHtml('');
                    receiptHtmlRef.current = '';
                });
        }
    }, [isOpen, saleId, tenantId, autoPrintEnabled, executePrint]);

    // Focus supervisor PIN input if requested
    useEffect(() => {
        if (showReprintPin && pinInputRef.current) {
            setTimeout(() => pinInputRef.current?.focus(), 100);
        }
    }, [showReprintPin]);

    // Keyboard shortcuts: Enter/Space = New Sale, P = Print, Esc = Close
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (showReprintPin) return; // Allow normal PIN typing

            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onNewSale();
            } else if (e.key === 'p' || e.key === 'P') {
                e.preventDefault();
                handlePrint();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, showReprintPin, onNewSale, onClose]);

    const handlePrint = () => {
        if (printCount === 0) {
            executePrint(1);
        } else {
            setShowReprintPin(true);
            setPinError('');
            setReprintPin('');
        }
    };

    const handleReprintWithPin = async () => {
        if (reprintPin.length < 4) {
            setPinError('PIN must be at least 4 digits');
            return;
        }

        setIsVerifyingPin(true);
        setPinError('');

        try {
            const { data } = await apiClient.post(`/tenants/${tenantId}/auth/verify-pin`, { pin: reprintPin });
            if (data?.valid) {
                executePrint(1);
                setShowReprintPin(false);
                setReprintPin('');
            } else {
                setPinError('Invalid supervisor PIN');
                setReprintPin('');
                pinInputRef.current?.focus();
            }
        } catch {
            setPinError('Verification failed. Please try again.');
        } finally {
            setIsVerifyingPin(false);
        }
    };

    const handlePinKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleReprintWithPin();
        if (e.key === 'Escape') {
            setShowReprintPin(false);
            setReprintPin('');
            setPinError('');
        }
    };

    const handleCopyReceipt = () => {
        if (receiptHtml) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = receiptHtml;
            const text = tempDiv.textContent || tempDiv.innerText || '';
            navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    const toggleAutoPrint = () => {
        const next = !autoPrintEnabled;
        setAutoPrintEnabled(next);
        localStorage.setItem('pos_auto_print', String(next));
    };

    const formatNaira = (val: number) => `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0, y: 15 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.92, opacity: 0, y: 15 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-slate-100"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-br from-emerald-500 via-teal-600 to-emerald-700 px-6 py-7 text-center relative overflow-hidden">
                            {/* Close cross top-right for convenience */}
                            <button
                                onClick={onClose}
                                className="absolute top-3 right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                                title="Close (Esc)"
                                type="button"
                            >
                                <X size={16} />
                            </button>

                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', delay: 0.1, stiffness: 220 }}
                                className="relative z-10"
                            >
                                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-3 ring-4 ring-white/30 shadow-inner">
                                    <CheckCircle size={30} className="text-white" />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.15 }}
                                className="relative z-10"
                            >
                                <h2 className="text-xl font-black text-white tracking-tight">Sale Completed!</h2>
                                <p className="text-3xl font-black text-white mt-0.5">{formatNaira(total)}</p>

                                <div className="mt-3 flex items-center justify-center gap-2">
                                    {isPrinting ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-white text-[11px] font-bold">
                                            <motion.div
                                                animate={{ rotate: 360 }}
                                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                                className="w-2.5 h-2.5 rounded-full border-2 border-white border-t-transparent"
                                            />
                                            Printing...
                                        </span>
                                    ) : printCount > 0 ? (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-white text-[11px] font-bold">
                                            <CheckCheck size={13} />
                                            Printed ({printCount})
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/15 rounded-full text-white/90 text-[11px] font-medium">
                                            Press Enter for Next Sale
                                        </span>
                                    )}
                                </div>
                            </motion.div>
                        </div>

                        {/* Actions */}
                        <div className="p-5 space-y-3">
                            {!showReprintPin ? (
                                <>
                                    {/* Primary Action - New Sale (Fastest path for cashiers) */}
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={onNewSale}
                                        className="flex items-center justify-center gap-2.5 w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl hover:from-emerald-700 hover:to-teal-700 transition-all font-black text-base shadow-lg shadow-emerald-600/25"
                                        type="button"
                                    >
                                        <ShoppingCart size={19} />
                                        Next Sale (Enter)
                                    </motion.button>

                                    {/* Secondary Buttons: Print & Copy */}
                                    <div className="grid grid-cols-2 gap-2.5">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handlePrint}
                                            disabled={isPrinting || !receiptHtml}
                                            className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl font-bold text-xs text-slate-700 transition-all disabled:opacity-50"
                                            type="button"
                                        >
                                            <Printer size={15} className="text-slate-500" />
                                            {printCount === 0 ? 'Print (P)' : 'Reprint'}
                                        </motion.button>

                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleCopyReceipt}
                                            disabled={!receiptHtml}
                                            className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl font-bold text-xs text-slate-700 transition-all disabled:opacity-50"
                                            type="button"
                                        >
                                            {copied ? (
                                                <>
                                                    <CheckCheck size={15} className="text-emerald-600" />
                                                    <span className="text-emerald-700">Copied!</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Copy size={15} className="text-slate-500" />
                                                    Copy Slip
                                                </>
                                            )}
                                        </motion.button>
                                    </div>

                                    {/* WhatsApp Receipt - Coming Soon */}
                                    <motion.button
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={() => {
                                            alert('WhatsApp Receipt Sharing is coming soon! Direct WhatsApp Cloud API messaging is currently in development.');
                                        }}
                                        className="flex items-center justify-between w-full py-2.5 px-3 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80 rounded-xl font-bold text-xs text-emerald-800 transition-all cursor-pointer"
                                        type="button"
                                    >
                                        <div className="flex items-center gap-2">
                                            <MessageSquare size={15} className="text-emerald-600" />
                                            <span>Send via WhatsApp</span>
                                        </div>
                                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-black bg-emerald-600 text-white rounded-full shadow-xs">
                                            Coming Soon
                                        </span>
                                    </motion.button>

                                    {/* Non-blocking Auto-Print Toggle */}
                                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-xl border border-slate-200/70 text-xs">
                                        <span className="text-slate-600 font-medium flex items-center gap-1.5">
                                            <Printer size={13} className="text-slate-400" />
                                            Auto-print on sale
                                        </span>
                                        <button
                                            type="button"
                                            onClick={toggleAutoPrint}
                                            className="text-slate-700 hover:text-slate-900 focus:outline-hidden"
                                            title="Toggle auto-print"
                                        >
                                            {autoPrintEnabled ? (
                                                <ToggleRight size={22} className="text-emerald-600 fill-emerald-100" />
                                            ) : (
                                                <ToggleLeft size={22} className="text-slate-400" />
                                            )}
                                        </button>
                                    </div>

                                    {/* Supervisor reprint note */}
                                    {printCount > 0 && !showReprintPin && (
                                        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                                            <Shield size={13} className="text-amber-600 flex-shrink-0" />
                                            <p className="text-[11px] text-amber-800">
                                                Additional reprints require supervisor PIN
                                            </p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* Supervisor PIN Entry */
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-3"
                                >
                                    <div className="text-center py-1">
                                        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-2 text-amber-700">
                                            <Lock size={18} />
                                        </div>
                                        <h3 className="text-sm font-bold text-slate-800">Supervisor PIN</h3>
                                        <p className="text-[11px] text-slate-500">Required to authorize reprint</p>
                                    </div>

                                    <input
                                        ref={pinInputRef}
                                        type="password"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={6}
                                        value={reprintPin}
                                        onChange={(e) => {
                                            setReprintPin(e.target.value.replace(/\D/g, ''));
                                            setPinError('');
                                        }}
                                        onKeyDown={handlePinKeyDown}
                                        placeholder="••••••"
                                        className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-lg font-bold text-center tracking-widest focus:ring-4 focus:ring-amber-500/10 focus:border-amber-400 transition-all"
                                    />

                                    {/* Number Pad */}
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '', 0, '⌫'].map((key, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => {
                                                    if (key === '⌫') setReprintPin(prev => prev.slice(0, -1));
                                                    else if (key !== '') setReprintPin(prev => (prev + key).slice(0, 6));
                                                }}
                                                disabled={key === ''}
                                                className={clsx(
                                                    'py-2.5 rounded-lg text-sm font-bold transition-all',
                                                    key === '' ? 'bg-transparent' :
                                                        key === '⌫' ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200' :
                                                            'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                                                )}
                                                type="button"
                                            >
                                                {key === '⌫' ? '⌫' : key}
                                            </button>
                                        ))}
                                    </div>

                                    {pinError && (
                                        <div className="flex items-center gap-1.5 p-2 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700">
                                            <AlertCircle size={13} className="flex-shrink-0" />
                                            {pinError}
                                        </div>
                                    )}

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => {
                                                setShowReprintPin(false);
                                                setReprintPin('');
                                                setPinError('');
                                            }}
                                            className="flex-1 py-2 border border-slate-200 rounded-xl font-bold text-xs text-slate-600 hover:bg-slate-50 transition-all"
                                            type="button"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleReprintWithPin}
                                            disabled={reprintPin.length < 4 || isVerifyingPin}
                                            className="flex-1 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-xs shadow hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50"
                                            type="button"
                                        >
                                            {isVerifyingPin ? 'Checking...' : 'Reprint'}
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}